import React, { useEffect, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Row,
  Spinner,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import {
  getAllPlans,
  initiateCheckout,
  verifyPayment,
} from "store/actions/subscriptionAction"
import { showAlert } from "store/reducers/alertReducer"
import { ROUTES } from "constants/routes"
import "./billing.scss"
import { getCurrencyLabel } from "utils/currency"

const BILLING_SUBSCRIPTION_KEY = "billingSubscriptionId"
const BILLING_INVOICE_KEY = "billingInvoiceId"

const resolvePaymentUrl = data => {
  if (!data) return ""
  if (data.paymentUrl) return data.paymentUrl
  const session = data.session
  if (!session || typeof session !== "object") return ""
  return (
    session.paymentUrl ||
    session.payment_url ||
    session.checkout_url ||
    session.redirect_url ||
    session.url ||
    session.transaction?.url ||
    ""
  )
}

const Checkout = () => {
  const { t } = useTranslation()
  document.title = t("Checkout ")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)
  const preselectedPlanId = searchParams.get("planId") || ""

  const { plans, plansLoading, checkoutLoading, checkoutData } = useSelector(
    state => state.Subscription
  )

  const [selectedPlanId, setSelectedPlanId] = useState(preselectedPlanId)
  const [verifying, setVerifying] = useState(false)
  const [redirecting, setRedirecting] = useState(false)

  useEffect(() => {
    if (plans.length === 0) {
      dispatch(getAllPlans())
    }
  }, [dispatch, plans.length])

  useEffect(() => {
    const paymentUrl = resolvePaymentUrl(checkoutData)
    if (paymentUrl) {
      setRedirecting(true)
      window.location.href = paymentUrl
    }
  }, [checkoutData])

  const handlePaymentVerified = () => {
    sessionStorage.removeItem(BILLING_SUBSCRIPTION_KEY)
    sessionStorage.removeItem(BILLING_INVOICE_KEY)
    dispatch(
      showAlert({
        text: t("Subscription activated successfully."),
        type: "success",
      })
    )
    navigate(ROUTES.BILLING_DASHBOARD)
  }

  const runTapVerification = async paymentId => {
    setVerifying(true)
    try {
      const action = await dispatch(
        verifyPayment({
          paymentId,
          subscriptionId:
            sessionStorage.getItem(BILLING_SUBSCRIPTION_KEY) || "",
          invoiceId: sessionStorage.getItem(BILLING_INVOICE_KEY) || "",
          gateway: "tap",
        })
      )
      const { status, message } = action.payload || {}
      if (status) {
        handlePaymentVerified()
      } else {
        dispatch(
          showAlert({
            text: message || t("Payment verification failed."),
            type: "error",
          })
        )
      }
    } catch {
      dispatch(
        showAlert({
          text: t("Payment verification failed."),
          type: "error",
        })
      )
    } finally {
      setVerifying(false)
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const paymentId = params.get("tap_id") || params.get("paymentId")
    if (paymentId) {
      runTapVerification(paymentId)
    }
  }, [location.search])

  const selectedPlan = plans.find(p => p._id === selectedPlanId)

  const handleSubmit = e => {
    e.preventDefault()
    if (!selectedPlanId) {
      dispatch(showAlert({ text: t("Please select a plan."), type: "error" }))
      return
    }
    dispatch(
      initiateCheckout({ planId: selectedPlanId, paymentGateway: "tap" })
    ).then(action => {
      const { status, message, data } = action.payload || {}
      if (!status) {
        dispatch(
          showAlert({
            text: message || t("Checkout initiation failed."),
            type: "error",
          })
        )
        return
      }
      if (data?.subscription?._id) {
        sessionStorage.setItem(
          BILLING_SUBSCRIPTION_KEY,
          String(data.subscription._id)
        )
      }
      if (data?.invoice?._id) {
        sessionStorage.setItem(BILLING_INVOICE_KEY, String(data.invoice._id))
      }
      if (data?.freePlan) {
        handlePaymentVerified()
        return
      }
      const paymentUrl = resolvePaymentUrl(data)
      if (paymentUrl) {
        setRedirecting(true)
        window.location.href = paymentUrl
        return
      }
      const chargeId = data?.session?.id
      if (chargeId) {
        runTapVerification(String(chargeId))
        return
      }
      dispatch(
        showAlert({
          text: t("Unable to start Tap payment. Please try again."),
          type: "error",
        })
      )
    })
  }

  const busy = checkoutLoading || verifying || redirecting

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <BackButton />
              <h4 className="mb-0">{t("Checkout")}</h4>
            </div>
            <span className="tap-badge">
              <i className="bx bx-credit-card" />
              {t("TAP")}
            </span>
          </div>

          {(verifying || redirecting) && (
            <div className="text-center py-3 mb-3">
              <Spinner color="primary" className="me-2" />
              {redirecting
                ? t("Redirecting to Tap secure checkout...")
                : t("Processing payment...")}
            </div>
          )}

          {plansLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Row>
              <Col lg={7}>
                <Card>
                  <CardBody>
                    <h5 className="mb-3">{t("Select a Plan")}</h5>
                    <Form onSubmit={handleSubmit}>
                      <FormGroup>
                        <Row>
                          {plans.map(plan => (
                            <Col key={plan._id} md={6} className="mb-3">
                              <div
                                className={`gateway-option ${
                                  selectedPlanId === plan._id ? "selected" : ""
                                }`}
                                onClick={() => setSelectedPlanId(plan._id)}
                              >
                                <Input
                                  type="radio"
                                  name="plan"
                                  value={plan._id}
                                  checked={selectedPlanId === plan._id}
                                  onChange={() => setSelectedPlanId(plan._id)}
                                />
                                <strong>{plan.name}</strong>
                                <div className="text-muted small">
                                  {getCurrencyLabel(plan.currency || "SAR")}{" "}
                                  {Number(plan.price || 0).toFixed(2)}{" "}
                                  {plan.billingCycle === "yearly"
                                    ? `/ ${t("year")}`
                                    : `/ ${t("month")}`}
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </FormGroup>

                      <p className="text-muted small mb-3">
                        {t(
                          "You will be redirected to Tap's secure payment page to complete your subscription. Your card will be saved securely with Tap for automatic monthly renewal — we do not store your card number."
                        )}
                      </p>

                      <Button
                        type="submit"
                        color="primary"
                        className="mt-2"
                        disabled={busy || !selectedPlanId}
                      >
                        {busy ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {t("Processing...")}
                          </>
                        ) : (
                          t("Proceed to Payment")
                        )}
                      </Button>
                    </Form>
                  </CardBody>
                </Card>
              </Col>

              <Col lg={5}>
                <Card>
                  <CardBody>
                    <h5 className="mb-3">{t("Order Summary")}</h5>
                    {selectedPlan ? (
                      <>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">{t("Plan")}</span>
                          <strong>{selectedPlan.name}</strong>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">{t("Billing Cycle")}</span>
                          <span>
                            {selectedPlan.billingCycle === "yearly"
                              ? t("Yearly")
                              : t("Monthly")}
                          </span>
                        </div>
                        <hr />
                        <div className="d-flex justify-content-between fw-bold">
                          <span>{t("Total")}</span>
                          <span>
                            {getCurrencyLabel(selectedPlan.currency || "SAR")}{" "}
                            {Number(selectedPlan.price || 0).toFixed(2)}
                          </span>
                        </div>
                      </>
                    ) : (
                      <p className="text-muted">{t("No plan selected.")}</p>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Checkout
